import { notification } from 'antd';
import { DownloadIcon } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { getWorkspaceMembersSelector } from '../../../../redux/general/selectors';
import { getTasksForReportRequest } from '../../../../redux/state-areas/reports/actions';
import { priorityMapping, stripHtml } from '../dashboard-widgets/utils';

const ExportReportButton = ({
  reportId,
  reportTitle,
  workspaceMembers,
  workspaceProjects,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isExporting, setIsExporting] = useState(false);

  const nameMapping = useMemo(() => {
    return workspaceMembers?.reduce(
      (result, member) => ({ ...result, [member?._id]: member?.fullName }),
      {},
    );
  }, [workspaceMembers]);

  const projectMapping = useMemo(() => {
    return workspaceProjects?.reduce(
      (result, project) => ({ ...result, [project?._id]: project?.title }),
      {},
    );
  }, [workspaceProjects]);

  const handleExportReport = useCallback(async () => {
    if (isExporting) {
      return;
    }
    setIsExporting(true);
    try {
      const tasks = await dispatch(getTasksForReportRequest(intl, reportId));

      const preparedTasksArray = tasks?.map((task) => ({
        'Title Identifier': task?.titleIdentifier || '',
        'Task Title': task?.taskTitle || '',
        Description: stripHtml(task?.taskDescription),
        Priority: priorityMapping[task?.priority] || '',
        Severity: task?.severity || '',
        Completed: task?.isCompleted ? 'Yes' : 'No',
        Assignees:
          task?.assignee?.map((a) => nameMapping[a.assigneeId]).join(', ') ||
          '',
        'Due Date': task?.dueDate || '',
        'Created At': task?.createdAt
          ? new Date(task?.createdAt).toLocaleString()
          : '',
        'Updated At': task?.updatedAt
          ? new Date(task?.updatedAt).toLocaleString()
          : '',
        Project: projectMapping[task?.project] || '',
        'Created By': nameMapping[task?.createdBy] || '',
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(preparedTasksArray);

      // Set column widths
      const colWidths = [
        { wch: 15 }, // Title Identifier
        { wch: 65 }, // Task Title
        { wch: 100 }, // Description
        { wch: 10 }, // Priority
        { wch: 10 }, // Severity
        { wch: 10 }, // Completed
        { wch: 40 }, // Assignees
        { wch: 20 }, // Due Date
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
        { wch: 25 }, // Project
        { wch: 25 }, // Created By
      ];
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tasks');

      // Generate Excel file
      XLSX.writeFile(
        wb,
        `${reportTitle?.replace(' ', '_') || `tasks_export_${new Date().toISOString().split('T')[0]}`}.xlsx`,
      );

      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      notification.error({
        message: intl.formatMessage({
          id: 'REPORT_EXPORT_FAILURE',
          defaultMessage: 'Failed to export tasks. Please try again later.',
        }),
        placement: 'bottomRight',
      });
      setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExporting, nameMapping, projectMapping, reportId, reportTitle]);

  return (
    <button
      className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
      disabled={isExporting}
      onClick={handleExportReport}
      title={intl.formatMessage({
        id: 'EXPORT_CHART',
        defaultMessage: 'Export Chart To CSV.',
      })}
    >
      <DownloadIcon
        className="text-gray-400 group-hover:text-indigo-600"
        size={16}
      />
    </button>
  );
};

const mapStateToProps = (state) => ({
  workspaceMembers: getWorkspaceMembersSelector(state),
  workspaceProjects: state.workspaceReducer.workspaceProjects,
});

export default connect(mapStateToProps)(ExportReportButton);
